import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, RefreshCw, Users, Film, Heart, MessageCircle, UserPlus, Coins, Wallet, ArrowRight, AlertCircle } from "lucide-react";

const _fmtInt = (n) => (typeof n === "number" ? n.toLocaleString() : "—");
const _fmtMoney = (n) =>
  typeof n === "number" ? `£${n.toFixed(2)}` : "—";
const _fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white border border-black/10 rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 label-overline text-muted-foreground">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="font-display text-3xl mt-2 tabular-nums">{value}</div>
    {sub ? <div className="text-xs text-muted-foreground mt-1">{sub}</div> : null}
  </div>
);

/** Inline SVG line-chart for 30-day daily buckets. Pure CSS/SVG, no
 *  external chart lib. Renders empty state when total is 0. */
const TrajectoryChart = ({ label, buckets, labels, color = "#FF2D7A", testid }) => {
  const total = buckets.reduce((s, n) => s + n, 0);
  const max = Math.max(1, ...buckets);
  const w = 100;
  const h = 32;
  // Build the polyline points across the SVG viewBox
  const pts = buckets
    .map((n, i) => {
      const x = (i / (buckets.length - 1)) * w;
      const y = h - (n / max) * (h - 2) - 1;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  // Build area-fill polygon (close to baseline)
  const areaPts = `0,${h} ${pts} ${w},${h}`;
  const todayCount = buckets[buckets.length - 1] || 0;
  const firstLabel = labels[0]
    ? new Date(labels[0]).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";

  return (
    <div className="bg-white border border-black/10 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <div className="label-overline text-muted-foreground">{label}</div>
        <div className="text-sm tabular-nums">
          <strong>{total}</strong>{" "}
          <span className="text-muted-foreground">last 30d</span>
        </div>
      </div>
      <div className="mt-3">
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: 56 }}
          aria-hidden="true"
        >
          <polygon points={areaPts} fill={color} fillOpacity="0.12" />
          <polyline
            points={pts}
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {/* Today dot */}
          <circle
            cx={w}
            cy={h - (todayCount / max) * (h - 2) - 1}
            r="1.6"
            fill={color}
          />
        </svg>
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
        <span>{firstLabel}</span>
        <span>today</span>
      </div>
    </div>
  );
};

const AdminPlyxHealth = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/plyx/health");
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Forbidden");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    load();
  }, [user, navigate]);

  if (error) {
    return (
      <div className="container-narrow py-20 text-center text-muted-foreground">
        <AlertCircle className="w-8 h-8 mx-auto mb-4 opacity-60" />
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="container-narrow py-20 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
      </div>
    );
  }

  const { users, videos, engagement_lifetime, engagement_7d, watch_to_earn, attribution, trajectory_30d, generated_at } = data;

  return (
    <div className="container-narrow py-10" data-testid="admin-plyx-health-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl">Plyx Health</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Is Plyx growing? Live numbers across users, content, engagement, and W2E credits.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/plyx"
            data-testid="health-link-to-overview"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 text-sm hover:bg-black/5"
          >
            ← Overview
          </Link>
          <Link
            to="/admin/plyx/nps"
            data-testid="health-link-to-nps"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 text-sm hover:bg-black/5"
          >
            NPS dashboard <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="health-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black text-white text-sm hover:opacity-90 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {trajectory_30d ? (
        <>
          <h2 className="text-xs label-overline text-muted-foreground mb-3">30-day trajectory</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <TrajectoryChart
              label="New users per day"
              buckets={trajectory_30d.users}
              labels={trajectory_30d.labels}
              color="#FF2D7A"
              testid="health-trajectory-users"
            />
            <TrajectoryChart
              label="New videos per day"
              buckets={trajectory_30d.videos}
              labels={trajectory_30d.labels}
              color="#00E0C7"
              testid="health-trajectory-videos"
            />
          </div>
        </>
      ) : null}

      <h2 className="text-xs label-overline text-muted-foreground mb-3">People</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Users} label="Total users" value={_fmtInt(users.total)} sub={`+${users.new_7d} this week · +${users.new_24h} today`} testid="health-stat-users-total" />
        <Stat icon={UserPlus} label="Active creators (30d)" value={_fmtInt(users.active_creators_30d)} sub="Posted at least 1 video" testid="health-stat-active-creators" />
        <Stat icon={UserPlus} label="New follows (7d)" value={_fmtInt(engagement_7d.new_follows)} sub="Social-graph growth" testid="health-stat-new-follows" />
        <Stat icon={Film} label="Videos" value={_fmtInt(videos.total)} sub={`+${videos.new_7d} this week`} testid="health-stat-videos" />
      </div>

      <h2 className="text-xs label-overline text-muted-foreground mb-3">Engagement</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Film} label="Lifetime views" value={_fmtInt(engagement_lifetime.views)} testid="health-stat-views" />
        <Stat icon={Heart} label="Likes (7d)" value={_fmtInt(engagement_7d.likes)} sub={`Lifetime: ${_fmtInt(engagement_lifetime.likes)}`} testid="health-stat-likes-7d" />
        <Stat icon={MessageCircle} label="Comments (7d)" value={_fmtInt(engagement_7d.comments)} sub={`Lifetime: ${_fmtInt(engagement_lifetime.comments)}`} testid="health-stat-comments-7d" />
        <Stat icon={Film} label="Lifetime shares" value={_fmtInt(engagement_lifetime.shares)} testid="health-stat-shares" />
      </div>

      <h2 className="text-xs label-overline text-muted-foreground mb-3">Watch-to-Earn flow</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Coins} label="Credits issued (lifetime)" value={_fmtMoney(watch_to_earn.issued_total_gbp)} sub={`This week: ${_fmtMoney(watch_to_earn.issued_7d_gbp)}`} testid="health-stat-w2e-issued" />
        <Stat icon={Coins} label="Credits redeemed" value={_fmtMoney(watch_to_earn.redeemed_total_gbp)} sub="Moved to AfriMarket" testid="health-stat-w2e-redeemed" />
        <Stat icon={Wallet} label="Outstanding balance" value={_fmtMoney(watch_to_earn.outstanding_balance_gbp)} sub="Owed to viewers, not yet redeemed" testid="health-stat-w2e-outstanding" />
        <Stat icon={Coins} label="Attribution credits (lifetime)" value={_fmtMoney(attribution.lifetime_gbp)} sub={`This week: ${_fmtMoney(attribution.last_7d_gbp)}`} testid="health-stat-attribution" />
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Generated at {_fmtWhen(generated_at)} · numbers refresh live on tap.
      </p>

      <EmbedSweepPanel />
      <RestoreEmbedsPanel />
    </div>
  );
};

/** iter-123 recovery tool — the old "one report deletes the clip"
 *  policy silently removed embeds that played fine for most viewers
 *  (in-app browsers / crawlers false-positive as blocked). One tap
 *  re-publishes every threshold-blocked embed; genuinely dead clips
 *  get re-removed by the sweep above or by 3 fresh reports. */
const RestoreEmbedsPanel = () => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const restore = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/plyx/restore-blocked-embeds");
      setResult(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-black/10 rounded-xl p-5 mt-4" data-testid="admin-restore-embeds-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-bold text-base">Restore over-removed clips</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Brings back every video that was auto-removed after a single
            viewer report (an old, too-aggressive rule). Run this ONCE after
            deploying the fix — genuinely broken clips will be filtered out
            again automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={restore}
          disabled={busy}
          data-testid="admin-restore-embeds-run"
          className="px-4 py-2 rounded-full bg-[#00A86B] text-white text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          {busy ? "Restoring…" : "Restore clips"}
        </button>
      </div>
      {result && (
        <p className="text-sm mt-3" data-testid="admin-restore-embeds-result">
          ✅ Restored <strong>{result.restored}</strong> clips — feed now has{" "}
          <strong>{result.published_total}</strong> published videos.
        </p>
      )}
    </div>
  );
};

/** Admin tool — kicks off and polls the one-time YouTube embed-blocked
 *  sweep. Every embed-restricted video gets pulled from the feed so
 *  new visitors never see the black-tile experience. */
const EmbedSweepPanel = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const poll = async () => {
    try {
      const r = await api.get("/admin/plyx/sweep-embed-blocked");
      setState(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, []);
  const kick = async () => {
    setLoading(true);
    try {
      const r = await api.post("/admin/plyx/sweep-embed-blocked");
      setState(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not start sweep");
    } finally { setLoading(false); }
  };
  if (!state) return null;
  const pct = state.total ? Math.round((state.checked / state.total) * 100) : 0;
  return (
    <div className="bg-white border border-black/10 rounded-xl p-5 mt-8" data-testid="admin-embed-sweep-panel">
      <div className="flex items-center justify-between gap-4 mb-3">
        <div>
          <h3 className="font-bold text-base">YouTube embed sweep</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Probes every YouTube clip in your feed via yt-dlp and pulls the
            embed-blocked ones (Vevo, music labels, removed videos) so new
            visitors never see a black tile.
          </p>
        </div>
        <button
          type="button"
          onClick={kick}
          disabled={loading || state.running}
          data-testid="admin-embed-sweep-run"
          className="px-4 py-2 rounded-full bg-[#FF2D7A] text-white text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          {state.running ? "Running…" : state.done ? "Re-run sweep" : "Run sweep"}
        </button>
      </div>
      {state.running || state.done ? (
        <div className="text-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-1 h-2 rounded-full bg-black/10 overflow-hidden">
              <div className="h-full bg-[#FF2D7A] transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="tabular-nums text-xs text-muted-foreground w-20 text-right">
              {state.checked}/{state.total}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            <strong className="text-black">{state.blocked}</strong> blocked clips pulled
            {state.done ? " · sweep complete" : state.running ? " · scanning…" : ""}
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No sweep run yet.</p>
      )}
    </div>
  );
};

export default AdminPlyxHealth;
